import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getCurrencySymbol } from '@shared/utils/currencyOptions';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding, type ShopTheme } from '../theme';
import type { ShopLegal } from '../legal';
import { useI18n } from '../i18n';
import { formatWhole } from '../i18n/format';
import VoucherCard from '../components/VoucherCard';
import {
  AccentButton,
  QuietButton,
  SectionHeading,
  ShopFooter,
  ShopHeader,
  SmallCaps,
} from '../components/ShopChrome';

interface GiftVoucherProduct {
  id: string;
  name: string;
  description?: string;
  amount: number;
  currency: string;
  image_url?: string;
  display_order: number;
}

interface GiftVoucherPackage {
  id: string;
  name: string;
  description?: string;
  final_amount: number;
  currency: string;
  image_url?: string;
  display_order: number;
}

interface ShopCustomization {
  intro_title?: string;
  intro_description?: string;
  hero_image_url?: string;
  logo_url?: string;
}

interface ShopData {
  location_id: string;
  company_id: string;
  products: GiftVoucherProduct[];
  packages: GiftVoucherPackage[];
  location_name?: string;
  location_address?: string;
  location_language?: string;
  customization?: ShopCustomization;
  branding?: ShopBranding;
  legal?: ShopLegal;
  custom_amount_enabled?: boolean;
  currency?: string;
  preset_amounts?: number[];
  custom_amount_minimum?: number;
  custom_amount_maximum?: number;
}

/** A product and a package are the same thing on the shelf: a name, a line and a price. */
interface ShelfItem {
  id: string;
  kind: 'product' | 'package';
  name: string;
  description?: string;
  amount: number;
  currency: string;
  imageUrl?: string;
}

const ShopPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { t, locale, applyVenueLocale } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [shopData, setShopData] = useState<ShopData | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');

  useEffect(() => {
    const fetchShopData = async () => {
      if (!locationId) {
        setError('Location ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${getApiBaseUrl()}/gift-vouchers/shop/${locationId}`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 403) {
            setError(t('shop.error_disabled'));
          } else if (response.status === 404) {
            setError(t('shop.error_not_found'));
          } else {
            setError(errorData.error?.message || t('shop.error_generic'));
          }
          setLoading(false);
          return;
        }

        const data = (await response.json()) as ShopData;
        setShopData(data);
        // The venue's own language, unless the guest asked for another one.
        applyVenueLocale(data.location_language);
      } catch (err) {
        console.error('Error fetching shop data:', err);
        setError(t('shop.error_generic'));
      } finally {
        setLoading(false);
      }
    };

    fetchShopData();
  }, [locationId, t, applyVenueLocale]);

  const theme = useMemo(() => resolveShopTheme(shopData?.branding), [shopData?.branding]);

  const items: ShelfItem[] = useMemo(() => {
    if (!shopData) return [];
    const products = shopData.products.map<ShelfItem>((p) => ({
      id: p.id,
      kind: 'product',
      name: p.name,
      description: p.description,
      amount: p.amount,
      currency: p.currency,
      imageUrl: p.image_url,
    }));
    const packages = shopData.packages.map<ShelfItem>((p) => ({
      id: p.id,
      kind: 'package',
      name: p.name,
      description: p.description,
      amount: p.final_amount,
      currency: p.currency,
      imageUrl: p.image_url,
    }));
    return [...products, ...packages];
  }, [shopData]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return <CentredState theme={theme} label={t('shop.loading')} spinner />;
  }

  if (error) {
    return <CentredState theme={theme} title={t('shop.unavailable_title')} label={error} />;
  }

  if (!shopData) return null;

  const {
    location_name: venueName,
    location_address: venueAddress,
    customization,
    legal,
    custom_amount_enabled: customAmountEnabled,
    currency: shopCurrency,
    preset_amounts,
    custom_amount_minimum,
    custom_amount_maximum,
  } = shopData;

  const currency = shopCurrency || items[0]?.currency || 'EUR';
  const currencySymbol = getCurrencySymbol(currency);
  const presetAmounts = preset_amounts?.length ? preset_amounts : [50, 75, 100, 150];
  const minimumAmount = custom_amount_minimum || 10;
  const maximumAmount = custom_amount_maximum || 1000;
  const heroImage = customization?.hero_image_url;
  const hasItems = items.length > 0;
  const itemsHaveImages = items.some((item) => !!item.imageUrl);
  const chosenAmount = selectedAmount ?? (customAmount ? parseFloat(customAmount) : null);

  const goToPurchase = (amount: number) => {
    navigate(`/shop/${locationId}/purchase/custom?amount=${amount}&currency=${currency}`);
  };

  const handlePreset = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setAmountError(null);
  };

  const handleContinue = () => {
    const amount = selectedAmount ?? parseFloat(customAmount);
    if (!amount || Number.isNaN(amount)) {
      setAmountError(t('shop.error_choose_amount'));
      return;
    }
    if (amount < minimumAmount) {
      setAmountError(t('shop.error_minimum', { amount: `${currencySymbol}${formatWhole(minimumAmount, locale)}` }));
      return;
    }
    if (amount > maximumAmount) {
      setAmountError(t('shop.error_maximum', { amount: `${currencySymbol}${formatWhole(maximumAmount, locale)}` }));
      return;
    }
    goToPurchase(amount);
  };

  const selectItem = (item: ShelfItem) => {
    navigate(`/shop/${locationId}/purchase/${item.id}?type=${item.kind}`);
  };

  const previewCard = (
    <VoucherCard
      theme={theme}
      amount={chosenAmount && chosenAmount > 0 ? chosenAmount : presetAmounts[Math.floor(presetAmounts.length / 2)] || 100}
      currency={currency}
      currencySymbol={currencySymbol}
      venueName={venueName || t('card.gift_voucher')}
      venueAddress={venueAddress}
      expiryDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
    />
  );

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <ShopHeader
        theme={theme}
        venueName={venueName}
        onHome={() => scrollTo('top')}
        onCheckVoucher={() => navigate('/voucher')}
      />

      <main id="top" className="flex flex-1 flex-col">
        {/* Hero — the room if the venue has a photograph, otherwise the voucher itself */}
        <section
          className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-14 ${heroImage ? 'lg:gap-0' : ''}`}
          style={heroImage ? undefined : { borderBottom: `1px solid ${theme.hairline}` }}
        >
          <div className="flex flex-col justify-center gap-6 px-5 py-12 sm:px-10 sm:py-16 lg:py-[76px]">
            <SmallCaps theme={theme}>
              {[venueName, venueAddress].filter(Boolean).join(' · ')}
            </SmallCaps>
            <h1
              className="text-balance"
              style={{
                margin: 0,
                font: `500 clamp(34px, 6vw, 52px)/1.08 ${theme.fontFamily}`,
                letterSpacing: '-.025em',
                color: theme.ink,
              }}
            >
              {customization?.intro_title || t('shop.title')}
            </h1>
            <p
              style={{
                margin: 0,
                maxWidth: '44ch',
                font: `400 clamp(15px, 2vw, 17px)/1.65 ${theme.fontFamily}`,
                color: theme.body,
              }}
            >
              {customization?.intro_description || t('shop.intro')}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <AccentButton theme={theme} onClick={() => scrollTo('amounts')}>
                {t('shop.choose_amount')}
              </AccentButton>
              {hasItems && (
                <QuietButton theme={theme} onClick={() => scrollTo('menus')} style={{ padding: '15px 22px' }}>
                  {t('shop.see_menus')}
                </QuietButton>
              )}
            </div>
            {heroImage && (
              <div
                className="flex flex-wrap gap-x-7 gap-y-1 pt-1.5"
                style={{ font: `400 13px/1.6 ${theme.fontFamily}`, color: theme.label }}
              >
                <span>{t('shop.fact_instant')}</span>
                <span>{t('shop.fact_message')}</span>
                <span>{t('shop.fact_valid')}</span>
              </div>
            )}
          </div>

          {heroImage ? (
            <div className="h-64 w-full sm:h-96 lg:h-full lg:min-h-[460px]">
              <img
                src={heroImage}
                alt={venueName || ''}
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
          ) : (
            <div className="px-5 pb-12 sm:px-10 lg:py-16 lg:pl-0">
              <div className="lg:rotate-[-1.2deg]">{previewCard}</div>
            </div>
          )}
        </section>

        <div className="flex flex-col gap-14 px-5 py-14 sm:px-10 sm:py-16">
          {/* Amounts */}
          {customAmountEnabled !== false && (
            <section id="amounts">
              <SectionHeading
                theme={theme}
                title={t('shop.amounts_heading')}
                aside={t('shop.amounts_aside', {
                  amount: `${currencySymbol}${formatWhole(minimumAmount, locale)}`,
                })}
              />
              <div className="mt-6 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-stretch">
                {presetAmounts.map((amount) => {
                  const active = selectedAmount === amount;
                  return (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => handlePreset(amount)}
                      className="px-6 py-5 text-left transition-colors sm:min-w-[132px]"
                      style={{
                        background: theme.surface,
                        border: `1px solid ${active ? theme.ink : theme.hairline}`,
                        borderRadius: theme.radius,
                        boxShadow: active ? `inset 0 0 0 1px ${theme.ink}` : undefined,
                      }}
                    >
                      <span
                        style={{
                          font: `500 clamp(24px, 5vw, 30px)/1 ${theme.fontFamily}`,
                          color: theme.ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {currencySymbol}
                        {formatWhole(amount, locale)}
                      </span>
                    </button>
                  );
                })}

                <div
                  className="col-span-2 flex items-center gap-1.5 px-6 py-5 sm:col-span-1 sm:w-[184px] sm:shrink-0"
                  style={{
                    background: theme.surface,
                    border: `1px solid ${customAmount ? theme.ink : theme.hairline}`,
                    borderRadius: theme.radius,
                  }}
                >
                  <span style={{ font: `500 clamp(24px, 5vw, 30px)/1 ${theme.fontFamily}`, color: theme.label }}>
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={minimumAmount}
                    max={maximumAmount}
                    step="1"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                      setAmountError(null);
                    }}
                    placeholder={t('shop.other_amount')}
                    className="w-full min-w-0 border-none bg-transparent p-0 outline-none"
                    style={{ font: `500 clamp(22px, 4.5vw, 30px)/1 ${theme.fontFamily}`, color: theme.ink }}
                  />
                </div>

                <AccentButton
                  theme={theme}
                  onClick={handleContinue}
                  className="hidden sm:inline-flex"
                  style={{ padding: '22px 30px' }}
                >
                  {t('common.continue')}
                </AccentButton>
              </div>
              {amountError && (
                <p className="mt-3" style={{ font: `400 13px/1.5 ${theme.fontFamily}`, color: theme.accent }}>
                  {amountError}
                </p>
              )}
            </section>
          )}

          {/* Set menus and packages */}
          {hasItems && (
            <section id="menus">
              <SectionHeading theme={theme} title={t('shop.menus_heading')} aside={t('shop.menus_aside')} />
              {itemsHaveImages ? (
                <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((item) => (
                    <button
                      key={`${item.kind}-${item.id}`}
                      type="button"
                      onClick={() => selectItem(item)}
                      className="flex flex-col overflow-hidden text-left"
                      style={{
                        background: theme.surface,
                        border: `1px solid ${theme.hairline}`,
                        borderRadius: theme.radius,
                      }}
                    >
                      {item.imageUrl && (
                        <img src={item.imageUrl} alt={item.name} className="h-48 w-full object-cover" loading="lazy" />
                      )}
                      <div className="flex flex-1 flex-col gap-2.5 p-6">
                        <span style={{ font: `500 19px/1.25 ${theme.fontFamily}`, color: theme.ink }}>{item.name}</span>
                        {item.description && (
                          <span className="flex-1" style={{ font: `400 14px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
                            {item.description}
                          </span>
                        )}
                        <div
                          className="mt-2 flex items-baseline justify-between pt-4"
                          style={{ borderTop: `1px solid ${theme.hairlineSoft}` }}
                        >
                          <span
                            style={{
                              font: `500 22px/1 ${theme.fontFamily}`,
                              color: theme.ink,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {getCurrencySymbol(item.currency)}
                            {formatWhole(item.amount, locale)}
                          </span>
                          <span
                            className="inline-flex items-center gap-1.5"
                            style={{ font: `600 13px/1 ${theme.fontFamily}`, color: theme.accent }}
                          >
                            {t('shop.select')} <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-1">
                  {items.map((item) => (
                    <button
                      key={`${item.kind}-${item.id}`}
                      type="button"
                      onClick={() => selectItem(item)}
                      className="grid w-full grid-cols-[1fr_auto] items-baseline gap-x-8 gap-y-1 py-5 text-left sm:grid-cols-[1fr_auto_auto]"
                      style={{ borderBottom: `1px solid ${theme.hairline}` }}
                    >
                      <div className="col-span-2 sm:col-span-1">
                        <div style={{ font: `500 19px/1.3 ${theme.fontFamily}`, color: theme.ink }}>{item.name}</div>
                        {item.description && (
                          <div
                            className="mt-1.5"
                            style={{ font: `400 14px/1.6 ${theme.fontFamily}`, color: theme.muted, maxWidth: '56ch' }}
                          >
                            {item.description}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          font: `500 22px/1 ${theme.fontFamily}`,
                          color: theme.ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {getCurrencySymbol(item.currency)}
                        {formatWhole(item.amount, locale)}
                      </div>
                      <span
                        className="inline-flex items-center gap-1.5 justify-self-end"
                        style={{ font: `600 13px/1 ${theme.fontFamily}`, color: theme.accent }}
                      >
                        {t('shop.select')} <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* What they receive — the card again where the hero is a photograph, facts otherwise */}
          {heroImage ? (
            <section>
              <SectionHeading theme={theme} title={t('shop.receive_heading')} aside={t('shop.receive_aside')} />
              <div className="mt-6 max-w-[820px]">{previewCard}</div>
            </section>
          ) : (
            <section className="grid gap-8 sm:grid-cols-3">
              {(['delivery', 'validity', 'redeeming'] as const).map((fact) => (
                <div key={fact} className="pt-4" style={{ borderTop: `1px solid ${theme.hairline}` }}>
                  <SmallCaps theme={theme}>{t(`shop.fact_${fact}_label`)}</SmallCaps>
                  <p className="mt-2.5" style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.body, margin: 0 }}>
                    {t(`shop.fact_${fact}_body`)}
                  </p>
                </div>
              ))}
            </section>
          )}

          {!hasItems && customAmountEnabled === false && (
            <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
              {t('shop.empty')}
            </p>
          )}
        </div>
      </main>

      <ShopFooter
        theme={theme}
        venueName={venueName}
        venueAddress={venueAddress}
        legal={legal}
        locationId={locationId}
      />

      {/* On a phone the amount lives at the top of the page and the action at the bottom */}
      {customAmountEnabled !== false && chosenAmount && chosenAmount > 0 && (
        <div
          className="sticky bottom-0 z-40 px-5 py-3 sm:hidden"
          style={{ background: theme.paper, borderTop: `1px solid ${theme.hairline}` }}
        >
          <AccentButton theme={theme} onClick={handleContinue} className="w-full" style={{ padding: '16px' }}>
            {t('shop.continue_with', {
              amount: `${currencySymbol}${formatWhole(chosenAmount, locale)}`,
            })}
          </AccentButton>
        </div>
      )}
    </div>
  );
};

const CentredState: React.FC<{ theme: ShopTheme; title?: string; label: string; spinner?: boolean }> = ({
  theme,
  title,
  label,
  spinner = false,
}) => (
  <div className="flex min-h-screen items-center justify-center px-5" style={themeCssVars(theme)}>
    <div className="max-w-md text-center">
      {spinner && <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin" style={{ color: theme.accent }} />}
      {title && (
        <h1 style={{ font: `500 26px/1.2 ${theme.fontFamily}`, color: theme.ink, margin: '0 0 8px' }}>{title}</h1>
      )}
      <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: 0 }}>{label}</p>
    </div>
  </div>
);

export default ShopPage;
